import { apiRequest } from '@scc/lib'
import type { CreatorRepository } from '@/data/repositories/types'
import type { Creator, CreatorSummary, CreatorPriorMetrics, CreatorVideo } from '@/modules/creators/types'
import { toApiDate } from '@/lib/api-date'
import type { DateRange } from '@/modules/shared/types'

function creatorsPath(range: DateRange): string {
  return `/analytics/creators?start=${toApiDate(range.from)}&end=${toApiDate(range.to)}`
}

function videosPath(username: string, range: DateRange): string {
  return `/analytics/creators/videos?username=${encodeURIComponent(username)}&start=${toApiDate(range.from)}&end=${toApiDate(range.to)}`
}

/** Raw video row from the API (numeric fields may arrive as strings from ClickHouse). */
interface ApiVideo {
  videoId: string
  title?: string
  postedAt?: string
  gmv?: string | number
  views?: string | number
  orders?: string | number
}

/** Newer backend wraps the videos; tolerate a bare array too. */
type ApiVideosResponse = ApiVideo[] | { videos: ApiVideo[] }

export function mapApiVideo(v: ApiVideo): CreatorVideo {
  return {
    videoId: v.videoId,
    title: v.title ?? '',
    postedAt: v.postedAt ?? '',
    gmv: Number(v.gmv) || 0,
    views: Number(v.views) || 0,
    orders: Number(v.orders) || 0,
  }
}

interface ApiCreator {
  username: string
  gmv: number
  orders: number
  views: number
  videos: number
  avgCtr: number
  gpm: number
  conversionRate: number
  avgOrderValue: number
  lastPostedAt: string
  topTier: boolean
  deltaVsPriorPeriod: number
  prior?: CreatorPriorMetrics | null
}

/** Newer backend wraps the rows alongside summary totals; older one returns a bare array. */
type ApiCreatorsResponse = ApiCreator[] | { creators: ApiCreator[]; summary: CreatorSummary }

/**
 * Fallback summary derived purely from the current-period rows — used only while an
 * older backend (bare array, no summary) is still live. Prior GMV is backed out of each
 * row's delta; counts/GPM fall back to current so the cards show a 0% (rather than empty) delta.
 */
function deriveSummary(creators: Creator[]): CreatorSummary {
  const totalGmv = creators.reduce((s, c) => s + c.gmv, 0)
  const totalGmvPrior = creators.reduce(
    (s, c) => s + (c.deltaVsPriorPeriod > -1 ? c.gmv / (1 + c.deltaVsPriorPeriod) : 0),
    0,
  )
  const avgGpm = creators.length ? creators.reduce((s, c) => s + c.gpm, 0) / creators.length : 0
  const topCreators = Math.ceil(creators.length * 0.1)
  return {
    totalCreators: creators.length,
    totalCreatorsPrior: creators.length,
    topCreators,
    topCreatorsPrior: topCreators,
    totalGmv,
    totalGmvPrior,
    avgGpm,
    avgGpmPrior: avgGpm,
  }
}

function rowsOf(res: ApiCreatorsResponse): ApiCreator[] {
  return Array.isArray(res) ? res : res.creators
}

export function mapApiCreator(c: ApiCreator, clientId: string): Creator {
  return {
    clientId,
    id: c.username,
    username: c.username,
    topTier: c.topTier,
    gmv: c.gmv,
    orders: c.orders,
    views: c.views,
    videos: c.videos,
    avgCtr: c.avgCtr,
    gpm: c.gpm,
    conversionRate: c.conversionRate,
    avgOrderValue: c.avgOrderValue,
    deltaVsPriorPeriod: c.deltaVsPriorPeriod,
    lastPostedAt: c.lastPostedAt,
    prior: c.prior ?? null,
  }
}

export function createApiCreatorRepository(): CreatorRepository {
  return {
    async getCreators(clientId, range) {
      const res = await apiRequest<ApiCreatorsResponse>(creatorsPath(range), {
        headers: { 'X-Store-Id': clientId },
      })
      const creators = rowsOf(res).map((c) => mapApiCreator(c, clientId))
      const summary = Array.isArray(res) ? deriveSummary(creators) : res.summary
      return { creators, summary }
    },
    async getCreatorById(clientId, creatorId, range) {
      const res = await apiRequest<ApiCreatorsResponse>(creatorsPath(range), {
        headers: { 'X-Store-Id': clientId },
      })
      const match = rowsOf(res).find((c) => c.username === creatorId)
      return match ? mapApiCreator(match, clientId) : null
    },
    async getCreatorVideos(clientId, username, range) {
      const res = await apiRequest<ApiVideosResponse>(videosPath(username, range), {
        headers: { 'X-Store-Id': clientId },
      })
      const rows = Array.isArray(res) ? res : res.videos
      return rows.map(mapApiVideo)
    },
    async getLiveCreators() { return [] },
    async getTargetCollabs() { return [] },
    async getCollaborationData() { return [] },
    async getCreatorIncentives() { return [] },
  }
}

export const creatorRepository = createApiCreatorRepository()
